/* oxlint-disable sorare/no-window-location */
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useKey } from 'react-use';
import styled, { css } from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconChevronDown } from '#src/atoms/icons/system/iconChevronDown.ts';
import { iconChevronRight } from '#src/atoms/icons/system/iconChevronRight.ts';
import { iconPlus } from '#src/atoms/icons/system/iconPlus.ts';
import { iconTrash } from '#src/atoms/icons/system/iconTrash.ts';
import { iconX } from '#src/atoms/icons/system/iconX.ts';
import { NativeSelect } from '#src/atoms/inputs/NativeSelect/index.tsx';
import { Switch } from '#src/atoms/inputs/Switch/index.tsx';
import { TextInput } from '#src/atoms/inputs/TextInput/index.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '#src/atoms/layout/flex/index.tsx';
import { LabelS, LabelXS } from '#src/atoms/typography/index.tsx';
import { Badge } from '#src/atoms/ui/Badge/index.tsx';
import { Chip } from '#src/atoms/ui/Chip/index.tsx';
import { ENV } from '#src/config.ts';
import { FLAG_VARIANTS } from '#src/contexts/featureFlags/__generated__/flagVariants.ts';
import { useIsMobile } from '#src/hooks/device/useIsMobile.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { LIFECYCLE, useLifecycle } from '#src/hooks/useLifecycle.ts';
import { useLocalStorage } from '#src/hooks/useLocalStorage.ts';
import { localStorage } from '#src/lib/storage/localStorage.ts';
import type {
  MobileAppVersion,
  SessionStorageEntries,
} from '#src/lib/storage/sessionStorage.ts';
import {
  IOsMobileAppVersion,
  sessionStorage,
} from '#src/lib/storage/sessionStorage.ts';
import { regularMaterial, thickMaterial } from '#src/style/utils.tsx';

const BUTTON_SIZE = 32;
const PILL_WIDTH = 140;
const VIEWPORT_MARGIN = 16;
const DRAG_THRESHOLD = 4;

const ENV_LABELS: Record<string, string> = {
  development: 'DEV',
  staging: 'STAGING',
  mockprod: 'MOCKPROD',
  production: 'PROD',
};
const ENV_COLORS: Record<string, string> = {
  development: 'var(--c-onBase-success)',
  staging: 'var(--c-rewards-orange)',
  mockprod: 'var(--c-score-low)',
  production: 'var(--c-red-400)',
};
const ENV_PORTS: Record<string, string> = {
  production: '3003',
  mockprod: '4003',
  staging: '5003',
};

const reload = () => window.location.reload();

const switchEnvPort = (port: string) => {
  const url = new URL(window.location.href);
  url.port = port;
  window.location.href = url.toString();
};

const isLocalhost = () => window.location.hostname.endsWith('localhost');

const currentWorktree = () => {
  const parts = window.location.hostname.split('.');
  return parts.length > 1 ? parts[0] : '';
};

const switchWorktree = (name: string) => {
  const url = new URL(window.location.href);
  const parts = url.hostname.split('.');
  const base = parts.length > 1 ? parts.slice(1).join('.') : url.hostname;
  url.hostname = name ? `${name}.${base}` : base;
  window.location.href = url.toString();
};

const Pill = styled.div`
  position: fixed;
  z-index: 100000;
  border-radius: 999px;
  ${regularMaterial};
  border: 1px solid var(--c-nd-200);
  color: var(--c-white);
  font: var(--t-label-xs);
  cursor: grab;
  touch-action: none;
  user-select: none;
  box-shadow: 0 4px 12px rgb(0 0 0 / 40%);
  &:active {
    cursor: grabbing;
  }
`;
const PillContent = styled(Vertical).attrs({ gap: 0.5 })`
  min-height: ${BUTTON_SIZE}px;
  justify-content: center;
  padding: var(--unit);
`;
const BranchTag = styled(LabelXS)`
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--c-nd-500);
`;
const EnvChipButton = styled(Chip).attrs({ as: 'button', type: 'button' })`
  border: none;
  cursor: pointer;
`;
const EnvDropdownWrapper = styled.div`
  position: relative;
`;
const EnvDropdown = styled(Vertical).attrs({ gap: 0.5 })`
  position: absolute;
  top: calc(100% + var(--half-unit));
  right: 0;
  z-index: 1;
  min-width: 132px;
  padding: var(--half-unit);
  border-radius: var(--half-unit);
  background: var(--c-nd-100-opaque);
  border: 1px solid var(--c-nd-200);
`;
const HideX = styled(ButtonBase)`
  display: flex;
  flex-shrink: 0;
  padding: calc(var(--half-unit) / 2);
  cursor: pointer;
  color: var(--c-nd-500);
  &:hover {
    color: var(--c-white);
  }
`;
const Panel = styled(Vertical).attrs({ gap: 2 })`
  position: fixed;
  z-index: 100000;
  width: min(360px, calc(100 * var(--vw) - var(--quadruple-unit)));
  max-height: min(80 * var(--vh), 720px);
  overflow-y: auto;
  padding: var(--double-unit);
  border-radius: var(--unit);
  ${thickMaterial};
  border: 1px solid var(--c-nd-200);
  box-shadow: 0 8px 24px rgb(0 0 0 / 50%);
`;
const SectionTitle = styled(LabelXS)`
  padding-left: var(--unit);
  color: var(--c-nd-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
const ItemCard = styled(Vertical)`
  background: var(--c-nd-100);
  border-radius: var(--unit);
  overflow: hidden;
`;
const itemRowStyle = css`
  width: 100%;
  min-height: 40px;
  padding: var(--unit) var(--double-unit);
  text-align: left;
`;
const ItemRow = styled(SBHorizontal)`
  ${itemRowStyle}
`;
const ItemRowButton = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${itemRowStyle}
  cursor: pointer;
  background: transparent;
  color: var(--c-white);
  font: inherit;
  &:hover {
    background: var(--c-nd-200);
  }
`;
const ItemValue = styled(Horizontal).attrs({ gap: 0.5 })`
  flex-shrink: 0;
  color: var(--c-nd-500);
`;
const Expanded = styled(Vertical).attrs({ gap: 1 })`
  padding: var(--unit) var(--double-unit) var(--double-unit);
  border-top: 1px solid var(--c-nd-200);
`;
const OptionRow = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: var(--half-unit) var(--unit);
  border-radius: var(--half-unit);
  text-align: left;
  cursor: pointer;
  background: transparent;
  color: var(--c-white);
  font: inherit;
  &:hover {
    background: var(--c-nd-200);
  }
`;
const Chevron = styled(FontAwesomeIcon)<{ $open: boolean }>`
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
`;
const ScrollList = styled(Vertical).attrs({ gap: 0.5 })`
  max-height: 168px;
  overflow-y: auto;
`;
const RemoveButton = styled(ButtonBase)`
  flex-shrink: 0;
  cursor: pointer;
  color: var(--c-nd-500);
  &:hover {
    color: var(--c-white);
  }
`;
const Mono = styled.span`
  font-family: monospace;
  word-break: break-all;
`;
const Footnote = ({ children }: { children: ReactNode }) => (
  <LabelXS color="var(--c-nd-500)">{children}</LabelXS>
);
const CompactInput = styled(TextInput)`
  height: 28px;
  padding: 0 var(--unit);
  font: var(--t-label-xs);
`;
const ItemSelect = styled(NativeSelect)`
  height: 28px;
  padding: 0 var(--triple-unit) 0 var(--unit);
  font: var(--t-label-xs);
  color: var(--c-nd-500);
  background-position: right var(--half-unit) center;
`;
const WideSelect = styled(ItemSelect)`
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ClearButton = styled(ButtonBase)`
  align-self: center;
  padding: var(--half-unit);
  color: var(--c-red-400);
  cursor: pointer;
  font: var(--t-label-xs);
  &:hover {
    text-decoration: underline;
  }
`;

const Section = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Vertical gap={1}>
    <SBHorizontal center>
      <SectionTitle>{title}</SectionTitle>
      {action}
    </SBHorizontal>
    <Vertical gap={1}>{children}</Vertical>
  </Vertical>
);

const ExpandableItem = ({
  title,
  value,
  children,
}: {
  title: string;
  value: ReactNode;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <ItemCard>
      <ItemRowButton onClick={() => setOpen(o => !o)}>
        <LabelS>{title}</LabelS>
        <ItemValue>
          <LabelXS color="var(--c-nd-500)">{value}</LabelXS>
          <Chevron
            $open={open}
            icon={iconChevronRight}
            size="xs"
            color="var(--c-nd-500)"
          />
        </ItemValue>
      </ItemRowButton>
      {!!open && <Expanded>{children}</Expanded>}
    </ItemCard>
  );
};

const ToggleItem = ({
  title,
  checked,
  onToggle,
}: {
  title: string;
  checked: boolean;
  onToggle: (next: boolean) => void;
}) => (
  <ItemCard>
    <ItemRow>
      <LabelS>{title}</LabelS>
      <Switch
        small
        checked={checked}
        onChange={e => onToggle(e.target.checked)}
      />
    </ItemRow>
  </ItemCard>
);

const parseValue = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const clampToViewport = (x: number, y: number) => ({
  x: Math.max(0, Math.min(x, window.innerWidth - PILL_WIDTH)),
  y: Math.max(0, Math.min(y, window.innerHeight - BUTTON_SIZE)),
});

type WorktreeInfo = { name: string; branch: string; apps: string[] };

const useWorktrees = (enabled: boolean): WorktreeInfo[] => {
  const [worktrees, setWorktrees] = useState<WorktreeInfo[]>([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    fetch('/__worktrees.json')
      .then(async r => {
        if (!r.ok) throw new Error('worktrees fetch failed');
        return r.json();
      })
      .then(data => {
        if (active) setWorktrees(data.worktrees ?? []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [enabled]);
  return worktrees;
};

const WorktreeItem = () => {
  const current = currentWorktree();
  const worktrees = useWorktrees(true);

  const options = [
    { value: '', label: 'main' },
    ...worktrees
      .filter(w => w.apps.length > 0)
      .map(w => ({
        value: w.name,
        label: w.branch && w.branch !== w.name ? w.branch : w.name,
      })),
  ];
  if (current && !options.some(o => o.value === current)) {
    options.splice(1, 0, { value: current, label: current });
  }

  return (
    <ItemCard>
      <ItemRow>
        <LabelS>Worktree</LabelS>
        <WideSelect
          value={current}
          values={options}
          onChange={switchWorktree}
        />
      </ItemRow>
    </ItemCard>
  );
};

type MobilePreset = {
  id: string;
  label: string;
  mobileApp: MobileAppVersion | null;
  twa: number | null;
};
const MOBILE_PRESETS: MobilePreset[] = [
  { id: 'web', label: 'Web', mobileApp: null, twa: null },
  { id: 'ios', label: 'iOS', mobileApp: IOsMobileAppVersion.IOS_1, twa: null },
  {
    id: 'ios-gold',
    label: 'iOS Gold',
    mobileApp: IOsMobileAppVersion.IOS_GOLD,
    twa: null,
  },
  { id: 'android', label: 'Android', mobileApp: '4', twa: null },
  { id: 'twa', label: 'TWA', mobileApp: null, twa: 1 },
];

const currentMobilePresetId = (): string => {
  if (sessionStorage.getItem('twa') !== null) return 'twa';
  const mobileApp = sessionStorage.getItem('mobileApp');
  if (mobileApp === IOsMobileAppVersion.IOS_GOLD) return 'ios-gold';
  if (mobileApp?.includes('ios')) return 'ios';
  if (mobileApp) return 'android';
  return 'web';
};

const AppPlatformItem = () => {
  const currentId = currentMobilePresetId();

  const apply = (id: string) => {
    const preset = MOBILE_PRESETS.find(p => p.id === id);
    if (!preset) return;
    if (preset.mobileApp) sessionStorage.setItem('mobileApp', preset.mobileApp);
    else sessionStorage.removeItem('mobileApp');
    if (preset.twa !== null) sessionStorage.setItem('twa', preset.twa);
    else sessionStorage.removeItem('twa');
    reload();
  };

  return (
    <ItemCard>
      <ItemRow>
        <LabelS>App platform</LabelS>
        <ItemSelect
          value={currentId}
          values={MOBILE_PRESETS.map(p => ({ value: p.id, label: p.label }))}
          onChange={apply}
        />
      </ItemRow>
    </ItemCard>
  );
};

const ForcedFlagsItem = () => {
  const { flags } = useFeatureFlags();
  const [forcedFlags, setForcedFlags] = useState<Record<string, string>>(
    () => sessionStorage.getItem('forcedFlag') || {}
  );
  const [key, setKey] = useState('');
  const [value, setValue] = useState(FLAG_VARIANTS['']?.[0]?.value ?? 'true');
  const [changed, setChanged] = useState(false);

  const knownKeys = useMemo(() => Object.keys(flags).sort(), [flags]);
  const entries = Object.entries(forcedFlags);
  const keyVariants = FLAG_VARIANTS[key];

  const selectKey = (k: string) => {
    setKey(k);
    setValue(FLAG_VARIANTS[k]?.[0]?.value ?? 'true');
  };

  const persist = (next: Record<string, string>) => {
    setForcedFlags(next);
    setChanged(true);
    sessionStorage.setItem('forcedFlag', next);
  };
  const add = () => {
    if (!key.trim()) return;
    persist({ ...forcedFlags, [key.trim()]: value.trim() });
    setKey('');
    setValue('true');
  };
  const remove = (k: string) => {
    const { [k]: _removed, ...rest } = forcedFlags;
    persist(rest);
  };

  return (
    <ExpandableItem title="Forced feature flags" value={entries.length}>
      {entries.map(([k, v]) => (
        <SBHorizontal key={k} center>
          <LabelXS as="div">
            <Mono>{k}</Mono> ={' '}
            <Mono>
              {FLAG_VARIANTS[k]?.find(variant => variant.value === v)?.name ??
                v}
            </Mono>
          </LabelXS>
          <RemoveButton
            type="button"
            aria-label={`Remove forced flag ${k}`}
            onClick={() => remove(k)}
          >
            <FontAwesomeIcon icon={iconTrash} size="sm" />
          </RemoveButton>
        </SBHorizontal>
      ))}
      <Horizontal gap={0.5}>
        <CompactInput
          list="debug-flag-keys"
          placeholder="flagName (camelCase)"
          value={key}
          onChange={e => selectKey(e.target.value)}
        />
        <datalist id="debug-flag-keys">
          {knownKeys.map(k => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </datalist>
        {keyVariants ? (
          <ItemSelect
            value={value}
            values={keyVariants.map(v => ({
              value: v.value,
              label: v.name ?? v.value,
            }))}
            onChange={setValue}
          />
        ) : (
          <CompactInput
            placeholder="value"
            value={value}
            onChange={e => setValue(e.target.value)}
          />
        )}
        <Button
          size="compact"
          color="quaternary"
          onClick={add}
          startIcon={<FontAwesomeIcon icon={iconPlus} size="xs" />}
        >
          Add
        </Button>
      </Horizontal>
      {!!changed && (
        <Button size="small" color="primary" fullWidth onClick={reload}>
          Reload to apply
        </Button>
      )}
      <Footnote>Stored in sessionStorage. Values parsed as JSON.</Footnote>
    </ExpandableItem>
  );
};

const LifecycleItem = () => {
  const { lifecycle, update, loading } = useLifecycle();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('true');

  const knownKeys = useMemo(() => Object.values(LIFECYCLE).sort(), []);
  const entries = Object.entries(lifecycle ?? {});

  const apply = () => {
    if (!key.trim()) return;
    (update as (name: string, v: unknown) => Promise<void>)(
      key.trim(),
      parseValue(value)
    );
  };

  return (
    <ExpandableItem
      title="Lifecycle override"
      value={lifecycle ? entries.length : '—'}
    >
      {!lifecycle ? (
        <Footnote>Sign in to read and override lifecycle.</Footnote>
      ) : (
        <>
          {entries.length > 0 && (
            <ScrollList>
              {entries.map(([k, v]) => (
                <LabelXS key={k} as="div">
                  <Mono>{k}</Mono> = <Mono>{JSON.stringify(v)}</Mono>
                </LabelXS>
              ))}
            </ScrollList>
          )}
          <Horizontal gap={0.5}>
            <CompactInput
              list="debug-lifecycle-keys"
              placeholder="lifecycle key"
              value={key}
              onChange={e => setKey(e.target.value)}
            />
            <datalist id="debug-lifecycle-keys">
              {knownKeys.map(k => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </datalist>
            <CompactInput
              placeholder="value"
              value={value}
              onChange={e => setValue(e.target.value)}
            />
            <Button
              size="compact"
              color="quaternary"
              loading={loading}
              onClick={apply}
            >
              Set
            </Button>
          </Horizontal>
          <Footnote>Persisted to your account (value parsed as JSON).</Footnote>
        </>
      )}
    </ExpandableItem>
  );
};

const EnvSwitcher = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <EnvDropdownWrapper>
      <EnvChipButton
        color="white"
        size="compact"
        aria-label="Switch environment"
        style={{ background: ENV_COLORS[ENV] }}
        onClick={() => setMenuOpen(o => !o)}
      >
        {ENV_LABELS[ENV] ?? ENV}
        <FontAwesomeIcon icon={iconChevronDown} size="xs" />
      </EnvChipButton>
      {!!menuOpen && (
        <EnvDropdown>
          {Object.entries(ENV_PORTS).map(([env, port]) => (
            <OptionRow key={env} onClick={() => switchEnvPort(port)}>
              <LabelXS as="span">{ENV_LABELS[env]}</LabelXS>
              <LabelXS as="span" color="var(--c-nd-500)">
                :{port}
              </LabelXS>
            </OptionRow>
          ))}
        </EnvDropdown>
      )}
    </EnvDropdownWrapper>
  );
};

const DEBUG_SESSION_KEYS: (keyof SessionStorageEntries)[] = [
  'forcedFlag',
  'mobileApp',
  'twa',
];

export const DebugMenu = () => {
  const [open, setOpen] = useState(false);
  const [storedPosition, setStoredPosition] = useLocalStorage(
    'debug-menu-position',
    undefined
  );
  const [position, setPosition] = useState(() => {
    if (storedPosition) return storedPosition;
    if (typeof window === 'undefined') return { x: 16, y: 16 };
    return {
      x: VIEWPORT_MARGIN,
      y: window.innerHeight - BUTTON_SIZE - VIEWPORT_MARGIN,
    };
  });

  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);

  const isMobile = useIsMobile();
  const worktrees = useWorktrees(isLocalhost());
  const worktree = currentWorktree();
  const branch = worktrees.find(w => w.name === worktree)?.branch;
  // Auto-generated worktrees use the worktree name as the branch; only show a
  // meaningful, user-chosen branch.
  const meaningfulBranch = branch && branch !== worktree ? branch : undefined;
  const [gqlDebug, setGqlDebug] = useLocalStorage('gql-debug', false);
  // Hidden is session-scoped: the close button keeps it hidden until the tab
  // is closed, Ctrl+D brings it back.
  const [hidden, setHiddenState] = useState(
    () => sessionStorage.getItem('debug-menu-hidden') === true
  );
  const setHidden = (value: boolean) => {
    sessionStorage.setItem('debug-menu-hidden', value);
    setHiddenState(value);
  };

  useKey(
    (e: KeyboardEvent) =>
      e.key.toLowerCase() === 'd' && (e.metaKey || e.ctrlKey),
    e => {
      e.preventDefault();
      setHidden(false);
      setOpen(v => !v);
    }
  );

  useKey('Escape', () => setOpen(false));

  const onPointerDown = (e: ReactPointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: position.x,
      originY: position.y,
      moved: false,
    };
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) drag.moved = true;
    setPosition(clampToViewport(drag.originX + dx, drag.originY + dy));
  };
  const onPointerUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (drag.moved) setStoredPosition(position);
    else setOpen(v => !v);
  };

  const clearAll = () => {
    DEBUG_SESSION_KEYS.forEach(k => sessionStorage.removeItem(k));
    sessionStorage.removeItem('debug-menu-hidden');
    localStorage.removeItem('gql-debug');
    localStorage.removeItem('debug-menu-position');
    reload();
  };

  const panelStyle =
    typeof window === 'undefined'
      ? undefined
      : {
          left:
            position.x > window.innerWidth / 2
              ? undefined
              : Math.min(position.x, window.innerWidth - 360),
          right:
            position.x > window.innerWidth / 2
              ? Math.max(0, window.innerWidth - position.x - PILL_WIDTH)
              : undefined,
          top:
            position.y < window.innerHeight / 2
              ? position.y + BUTTON_SIZE + 8
              : undefined,
          bottom:
            position.y < window.innerHeight / 2
              ? undefined
              : Math.max(8, window.innerHeight - position.y + 8),
        };

  const overrideCount =
    Object.keys(sessionStorage.getItem('forcedFlag') || {}).length +
    (currentMobilePresetId() === 'web' ? 0 : 1) +
    (gqlDebug ? 1 : 0);

  if (isMobile) return null;

  // Deployed staging/mockprod expose the menu through Ctrl+D only; the pill stays local-dev only.
  const showPill = !!import.meta.env.DEV && !hidden;

  return (
    <>
      {!!showPill && (
        <Pill
          aria-label="Debug menu"
          style={{ left: position.x, top: position.y }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          title="Debug menu (drag to move, click or Ctrl+D to open)"
        >
          <Badge
            size="small"
            badgeContent={
              !open && overrideCount > 0 ? overrideCount : undefined
            }
          >
            <PillContent>
              <Horizontal gap={0.5}>
                <Chip
                  color="white"
                  size="compact"
                  style={{ background: ENV_COLORS[ENV] }}
                >
                  {ENV_LABELS[ENV] ?? ENV}
                </Chip>
                {!!meaningfulBranch && (
                  <BranchTag>{meaningfulBranch}</BranchTag>
                )}
                <HideX
                  type="button"
                  aria-label="Hide debug menu for this session (Ctrl+D to show)"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={() => {
                    setOpen(false);
                    setHidden(true);
                  }}
                >
                  <FontAwesomeIcon icon={iconX} size="sm" />
                </HideX>
              </Horizontal>
            </PillContent>
          </Badge>
        </Pill>
      )}
      {!!open && (
        <Panel style={panelStyle}>
          <Section title="Environment" action={<EnvSwitcher />}>
            {isLocalhost() && <WorktreeItem />}
            <AppPlatformItem />
          </Section>

          <Section title="State">
            <ForcedFlagsItem />
            <LifecycleItem />
          </Section>

          <Section title="Debug">
            <ToggleItem
              title="Show GraphQL debug"
              checked={gqlDebug}
              onToggle={next => {
                setGqlDebug(next);
                reload();
              }}
            />
          </Section>

          <ClearButton type="button" onClick={clearAll}>
            Clear all debug states &amp; reload
          </ClearButton>
        </Panel>
      )}
    </>
  );
};
